import { Injectable } from '@angular/core';
import { protectedResources } from "../../auth-config";
import { HttpClient } from "@angular/common/http";
import { Observable, of, tap, throwError } from "rxjs";
import camelcaseKeys from "camelcase-keys";
import { map } from "rxjs/operators";
import { CadModel, CadModelRevision, Drawing } from "../../shared/models/cad-model.model";
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root'
})
export class CadModelService {
  brennerSoftwareApiEndpoint: string = protectedResources.BrennerSoftwareApi.endpoint;

  constructor(
    private http: HttpClient
  ) { }

  get() {
    return this.http.get<any[]>(this.brennerSoftwareApiEndpoint + `cadModel/getList/`)
      .pipe(map(x => this.cadModeRevisionResponse(x)));
  }

  getCadModelRevisionWithMessages(cadModelRevisionId: any) {
    return this.http.get<any>(this.brennerSoftwareApiEndpoint + `cadModelRevision/getIncludeMessages/${cadModelRevisionId}`);
  }

  getCadModelRevisionWithDrawings(cadModelRevisionId: any) {
    return this.http.get<any>(this.brennerSoftwareApiEndpoint + `cadModelRevision/getIncludeDrawings/${cadModelRevisionId.toString()}`);
  }

  download(id: string, fileName: string) {
    return this.http.get(this.brennerSoftwareApiEndpoint + `cadModelRevision/download/${id}`, { responseType: "blob", headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } })
      .pipe(tap(x => saveAs(x, fileName)));
  }

  update(updatedCadModel: any): Observable<CadModel> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + 'cadModel/modify/',
      camelcaseKeys(updatedCadModel as any, { deep: true, pascalCase: true }))
  }

  create(newCadModel: CadModel): Observable<any> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + 'cadModel/create/',
      camelcaseKeys(newCadModel as any, { deep: true, pascalCase: true }));
  }

  remove(cadModelId: string): Observable<any> {
    return this.http.delete<any>(this.brennerSoftwareApiEndpoint + `cadModel/delete/${cadModelId}/`)
      .pipe(map(cadModelDeleteResponse => cadModelDeleteResponse.data?.id));
  }

  updateRevision(updatedCadModel: any): Observable<CadModelRevision> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + 'cadModelRevision/modify/',
      camelcaseKeys(updatedCadModel as any, { deep: true, pascalCase: true }))
  }

  createRevision(newCadModelRevision: CadModelRevision): Observable<any> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + 'cadModelRevision/create/',
      camelcaseKeys(newCadModelRevision as any, { deep: true, pascalCase: true }))
      .pipe(map(x => this.cadModeRevisionResponse([x])[0]));
  }

  removeRevision(cadModelRevisionId: string): Observable<any> {
    return this.http.delete<any>(this.brennerSoftwareApiEndpoint + `cadModelRevision/delete/${cadModelRevisionId}/`)
      .pipe(map(cadModelDeleteResponse => cadModelDeleteResponse.data));
  }

  verifyDeleteRevisionIsValid(cadModelRevisionId: string): Observable<any> {
    return this.http.get<any>(this.brennerSoftwareApiEndpoint + `cadModelRevision/verifyDeleteIsValid/${cadModelRevisionId}`);
  }

  uploadRevision(file: any, cadModelRevisionId: string) {
    let form = new FormData()
    form.append('file', file.files[0])
    return this.http.post<any>(
      this.brennerSoftwareApiEndpoint + `cadModelRevision/upload/${cadModelRevisionId}`,
      form,
      {
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    );
  }

  validateRevision(cadModelRevisionId: string) {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + `cadModelRevision/validate/${cadModelRevisionId}`, {})
  }

  downloadDrawing(id: string, fileName: string) {
    return this.http.get(this.brennerSoftwareApiEndpoint + `drawing/download/${id}`, { responseType: "blob" })
      .pipe(tap(x => saveAs(x, fileName)));
  }

  removeBulkCadModelRevisionDrawings(drawingIds: string[]): Observable<any> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + `drawing/deleteBulk/`, {
      DrawingIds: drawingIds
    });
  }

  modifyCadModelRevisionDrawing(drawing: Drawing): Observable<any> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + 'drawing/modify/',
      camelcaseKeys(drawing as any, { deep: true, pascalCase: true }))
  }

  uploadDrawings(cadModelRevisionId: string, files: any) {
    let form = new FormData()
    files.files.forEach(file => {
      form.append('files', file)
    });
    return this.http.post<any>(
      this.brennerSoftwareApiEndpoint + `drawing/upload/${cadModelRevisionId}`,
      form,
      {
        headers: { 'Content-Disposition': 'multipart/form-data' },
      }
    )
  }

  archive(cadModelId: string): Observable<string> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + `cadModel/archive/${cadModelId}/`, {})
      .pipe(map(cadModelArchiveResponse => cadModelArchiveResponse.data));
  }

  unArchive(cadModelId: string): Observable<string> {
    return this.http.post<any>(this.brennerSoftwareApiEndpoint + `cadModel/unArchive/${cadModelId}/`, {})
      .pipe(map(cadModelArchiveResponse => cadModelArchiveResponse.data));
  }

  getArchivedCadModels(): Observable<CadModel[]> {
    return this.http.get<any[]>(this.brennerSoftwareApiEndpoint + `cadModel/getArchivedList/`)
      .pipe(map(x => this.cadModeRevisionResponse(x)));
  }

  cadModeRevisionResponse(cadModels: any[]): CadModel[] {
    return cadModels.map(cadModel => {
      try {
        return {
          ...cadModel,
          revisions: cadModel.revisions.map(revision => {
            if (revision.validationMessages) {
              const validationMessagesParsed = JSON.parse(revision.validationMessages) as string[];
              return {
                ...revision,
                validationMessages: validationMessagesParsed ?? [] as string[]
              } as CadModelRevision[]
            } else {
              return revision;
            }

          })
        }
      }
      catch (ex) {
        return cadModel;
      }
    })
  }

  generateIndentedBomReport(cadModelId: string): Observable<any> {
    return this.http.post<any>(
      this.brennerSoftwareApiEndpoint + `cadModel/generateIndentedBomReport/${cadModelId}`,
      {}
    );
  }

  generateIndentedBomReportRevision(cadModelRevisionId: string): Observable<any> {
    return this.http.post<any>(
      this.brennerSoftwareApiEndpoint + `cadModelRevision/generateIndentedBomReport/${cadModelRevisionId}`,
      {}
    );
  }
}
